import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Navigation, MapPin, SkipForward, Inbox, RefreshCw, SearchX, Volume2, VolumeX, ArrowLeft } from 'lucide-react';
import DictateField from './DictateField';
import { routeLink } from '../lib/route';
import { H, activeLoggers, loggerState, patchLogger, LOG_COLORS } from '../hooks/useLoggers';
import { buzz } from '../lib/glove';
import { usePos, dist, fmtDist, ModePick, PosPick, pickAt } from './lpShared';

const queueOf = (project, onlyDue) => activeLoggers(project)
  .filter((l) => !l.missingAt && (!onlyDue || ['red', 'yellow'].includes(loggerState(l))))
  .sort((a, b) => (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity));

function Finding({ t, noise, setNoise, note, setNote }) {
  return (
    <div className="lc-find" data-testid="lc-finding">
      <b className="lc-lbl">{t.lcFinding}</b>
      <div className="lc-noise">
        <button type="button" className={`lc-nb yes ${noise === true ? 'on' : ''}`} data-testid="lc-noise-yes" onClick={() => setNoise(noise === true ? null : true)}><Volume2 size={18} /> {t.lcNoise}</button>
        <button type="button" className={`lc-nb no ${noise === false ? 'on' : ''}`} data-testid="lc-noise-no" onClick={() => setNoise(noise === false ? null : false)}><VolumeX size={18} /> {t.lcQuiet}</button>
      </div>
      <DictateField t={t} value={note} onChange={setNote} testid="lc-note-input" placeholder={t.lcNote} />
    </div>
  );
}

function MoveStep({ t, l, pos, mapRef, onDone, onBack }) {
  const [src, setSrc] = useState(pos ? 'gps' : 'cross');
  const [md, setMd] = useState({ mode: 'lift', days: Math.max(1, Math.round((l.durMs || 24 * H) / (24 * H))) });
  useEffect(() => { if (!pos && src === 'gps') setSrc('cross'); }, [pos, src]);
  return (
    <div className="lp-deploy" data-testid="lc-move-step">
      <b className="lp-next">{l.name}</b>
      <p className="muted lp-hint">{t.lcMoveHint}</p>
      <PosPick t={t} pos={pos} src={src} setSrc={setSrc} pre="lc" />
      <ModePick t={t} v={md} onChange={setMd} pre="lcm" />
      <button className="btn btn-green lp-big" data-testid="lc-move-set-btn" onClick={() => onDone(pickAt(src, pos, mapRef), md)}><RefreshCw size={18} /> {t.lcMoveSet}</button>
      <button className="btn btn-outline" data-testid="lc-move-back-btn" onClick={onBack}><ArrowLeft size={16} /> {t.lcBack}</button>
    </div>
  );
}

export default function LoggerCollect({ t, project, updateProject, mapRef, focusId }) {
  const [onlyDue, setOnlyDue] = useState(false);
  const [skipped, setSkipped] = useState([]);
  const [noise, setNoise] = useState(null);
  const [note, setNote] = useState('');
  const [moving, setMoving] = useState(false);
  const [focus, setFocus] = useState(focusId);
  useEffect(() => { setFocus(focusId); }, [focusId]);
  const near = useRef(false);
  const q = queueOf(project, onlyDue);
  const rank = (l) => (l.id === focus ? -1 : skipped.indexOf(l.id) + 1);
  const ordered = [...q].sort((a, b) => rank(a) - rank(b));
  const next = ordered[0];
  const pos = usePos(!!next);
  const d = next && pos ? dist(pos, next) : null, here = d != null && d < 30;
  const missing = activeLoggers(project).filter((l) => l.missingAt).length;
  useEffect(() => { setNoise(null); setNote(''); setMoving(false); near.current = false; if (next) mapRef.current?.setView(next.lat, next.lon, 19); }, [next?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (here && !near.current) { near.current = true; navigator.vibrate?.([200, 80, 200]); toast.success(t.lcArrived, { id: 'lc-arr' }); } }, [here, t]);

  useEffect(() => { document.querySelector('.lp-panel')?.scrollTo(0, 0); }, [moving, next?.id]);
  const finding = () => ({ noise, note: note.trim(), at: Date.now() });
  const done = (fn, msg) => { setMoving(false); setNoise(null); setNote(''); patchLogger(updateProject, next.id, fn); setSkipped((s) => s.filter((x) => x !== next.id)); buzz([60, 50, 120]); toast.success(`${msg}: ${next.name}`); };
  const collect = () => done(() => ({ collectedAt: Date.now(), finding: finding() }), t.lgCollected);
  const lost = () => done(() => ({ missingAt: Date.now(), finding: finding() }), t.lcMissingOk);
  const relocate = (at, md) => {
    const now = Date.now();
    done((l) => ({ history: [...(l.history || []), { lat: l.lat, lon: l.lon, placedAt: l.placedAt, removedAt: now, finding: finding() }], lat: at.lat, lon: at.lon, placedAt: now, mode: md.mode, durMs: md.mode === 'perm' ? null : md.days * 24 * H, dueAt: md.mode === 'perm' ? null : now + md.days * 24 * H }), t.lgMoved);
    mapRef.current.setView(at.lat, at.lon, 19);
  };

  const filter = (
    <div className="lg-durs" data-testid="lc-filter">
      <button type="button" className={`chip seg ${!onlyDue ? 'on' : ''}`} data-testid="lc-filter-all" onClick={() => setOnlyDue(false)}>{t.lcAll}</button>
      <button type="button" className={`chip seg ${onlyDue ? 'on' : ''}`} data-testid="lc-filter-due" onClick={() => setOnlyDue(true)}>{t.lcOnlyDue}</button>
    </div>
  );
  const foot = missing > 0 && <small className="lc-missing-n" data-testid="lc-missing-count">⚠ {missing} {t.lcMissingN}</small>;
  if (!next) return <>{filter}<p className="lp-done" data-testid="lc-all-done">✅ {t.lcAllDone}</p>{foot}</>;
  if (moving) return <MoveStep t={t} l={next} pos={pos} mapRef={mapRef} onDone={relocate} onBack={() => setMoving(false)} />;
  const st = loggerState(next);
  return (
    <div className={`lp-deploy ${here ? 'is-here' : ''}`} data-testid="lc-collect">
      {filter}
      <small className="muted">{ordered.length} {t.lcLeft}</small>
      <b className="lp-next" data-testid="lc-next-name"><i className="lc-dot" style={{ background: LOG_COLORS[st] }} /> {next.name}</b>
      <span className="muted" data-testid="lc-next-status">{t['lgSt_' + st]}{next.dueAt ? ` · ${t.lgDue} ${new Date(next.dueAt).toLocaleString(t.locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}</span>
      <p className="lp-dist" data-testid="lc-dist">{d == null ? t.skWait : here ? `📍 ${t.lcArrived}` : fmtDist(d)}</p>
      <div className="lp-row">
        <a className="btn btn-primary" data-testid="lc-nav-btn" href={routeLink(next)} target="_blank" rel="noreferrer"><Navigation size={16} /> {t.alNav}</a>
        <button className="btn btn-outline" data-testid="lc-show-btn" onClick={() => mapRef.current.setView(next.lat, next.lon, 19)}><MapPin size={16} /></button>
        <button className="btn btn-outline" data-testid="lc-skip-btn" title={t.lpSkip} disabled={ordered.length < 2} onClick={() => { setFocus(null); setSkipped((s) => [...s.filter((x) => x !== next.id), next.id]); }}><SkipForward size={16} /></button>
      </div>
      <Finding t={t} noise={noise} setNoise={setNoise} note={note} setNote={setNote} />
      <button className="btn btn-green lp-big" data-testid="lc-collect-btn" onClick={collect}><Inbox size={20} /> {t.lcDone}</button>
      {next.mode !== 'perm' && <button className="btn btn-primary" data-testid="lc-move-btn" onClick={() => setMoving(true)}><RefreshCw size={16} /> {t.lcMove}</button>}
      <button className="btn btn-outline danger" data-testid="lc-missing-btn" onClick={lost}><SearchX size={16} /> {t.lcMissing}</button>
      {foot}
    </div>
  );
}
